import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  UserCog,
  Shield,
  Pencil,
  Trash2,
  UserPlus,
  Loader2,
} from "lucide-react";

/**
 * Vendedores/Usuarios management section. Admin-only.
 */
export function VendedoresSection({
  users,
  usersLoading,
  currentAdmin,
  onNewUser,
  onEditUser,
  onDeleteUser,
}) {
  if ((currentAdmin?.role || "admin") !== "admin") return null;
  return (
    <section data-testid="section-vendedores">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-heading text-sky-900 mb-1 flex items-center gap-2">
            <UserCog className="w-7 h-7 text-sky-600" /> Vendedores y Usuarios
          </h1>
          <p className="text-sky-700/70">
            Administrá quién puede entrar al backoffice y cuántos clientes lleva
            cada vendedor.
          </p>
        </div>
        <Button
          onClick={onNewUser}
          className="btn-sky text-white"
          data-testid="new-user-button"
        >
          <UserPlus className="w-4 h-4 mr-1" /> Nuevo usuario
        </Button>
      </div>

      <Card className="glass-card border-0">
        <CardContent className="pt-6">
          {usersLoading ? (
            <div className="py-10 text-center text-sky-700/70">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Cargando...
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-sky-700/60 py-10">
              Todavía no hay vendedores cargados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-sky-200 text-sky-900">
                    <th className="text-left py-2 px-3">Usuario</th>
                    <th className="text-left py-2 px-3">Nombre</th>
                    <th className="text-left py-2 px-3">Email</th>
                    <th className="text-left py-2 px-3">Rol</th>
                    <th className="text-right py-2 px-3">Clientes</th>
                    <th className="text-right py-2 px-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-sky-100 hover:bg-sky-50/50"
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="py-2 px-3 font-medium text-sky-900">
                        {u.username}
                      </td>
                      <td className="py-2 px-3">{u.full_name || "—"}</td>
                      <td className="py-2 px-3 text-sky-700">{u.email}</td>
                      <td className="py-2 px-3">
                        {(u.role || "admin") === "admin" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                            <Shield className="w-3 h-3" /> Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                            <UserCog className="w-3 h-3" /> Vendedor
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-sky-700">
                        {u.clients_count ?? 0}
                      </td>
                      <td className="py-2 px-3 text-right space-x-1 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onEditUser(u)}
                          className="border-sky-200 text-sky-700 hover:bg-sky-50 h-7 px-2 text-xs"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Editar
                        </Button>
                        {u.id !== currentAdmin?.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onDeleteUser(u)}
                            className="border-red-200 text-red-600 hover:bg-red-50 h-7 px-2 text-xs"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-sky-700/70 mt-4">
            Los vendedores solo ven los clientes que ellos mismos cargaron. Los
            administradores ven todo.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
